(function() {
	'use strict';

	angular
		.module('components')
		.service('AccessoCalendarDataService', AccessoCalendarDataService);

	/**
	 * The Accesso Calendar Data Service
	 * @constructor
	 * @todo More response objects need to be supported
	 * @param {Filter} $filter The filter service
	 * @param {Service} AccessoCalendarService AccessoCalendarService
	 * @param {Service} AppData AppData
	 * @param {Service} DynamicCapacityPricingService DynamicCapacityPricingService Service
	 * @param {Service} Enums Enums
	 * @param {Service} JSONValidator JSONValidator
	 * @param {Service} Language Language Service
	 * @param {Service} Package Package
	 * @param {Service} Utilities Utilities service
	 * @param {Service} UtilityFunctions Functions
	 * @param {Service} UtilsPricing Pricing utilities
	 * @param {Service} UtilsValidation Validation utilities
	 * @returns {Service} The Accesso Calendar Data Service
	 * @ngInject
	 */
	function AccessoCalendarDataService(
		$filter,
		AccessoCalendarService,
		AppData,
		DynamicCapacityPricingService,
		Enums,
		JSONValidator,
		Language,
		Package,
		Utilities,
		UtilityFunctions,
		UtilsPricing,
		UtilsValidation
	) {
		var $currency = $filter('currencyFormatter'),
			$date = $filter('date'),
			checkProp = Utilities.checkProp,
			copy = angular.copy,
			filterById = UtilityFunctions.filterById,
			first = Utilities.first,
			forceInt = Utilities.forceInt,
			getLocale = Language.getLocaleWithSuffixAndFallback,
			getPrice = UtilsPricing.getPrice,
			localeExists = Language.localeExists,
			merge = Utilities.merge,
			mysqlToJsDate = Utilities.getJavaScriptDateFromMySQLDate,
			validArray = UtilsValidation.validArray,
			validObject = UtilsValidation.validObject,
			wrapToArray = Utilities.wrapObjectToArray;

		var service = {
			createCalendarDateObject: createCalendarDateObject,
			createCalendarTimeObject: createCalendarTimeObject,
			filterSameDayPackageList: filterSameDayPackageList,
			generateEmptyDaysForSet: generateEmptyDaysForSet,
			getAriaLabel: getAriaLabel,
			getFirstAvailableDate: getFirstAvailableDate,
			getLimitedAvailability: getLimitedAvailability,
			getLimitedAvailabilityThreshold: getLimitedAvailabilityThreshold,
			hasAvailability: hasAvailability,
			hasLimited: hasLimited,
			isDateInData: isDateInData,
			normalizeDataFromModel: normalizeDataFromModel,
			normalizeDataFromResponse: normalizeDataFromResponse,

			// PRIVATE
			_: {
				/* eslint-disable sort-keys */
				// PROPERTIES
				limitedAvailabilityThreshold: null,

				// MODEL METHODS
				model: {
					getModelNormalizer: _getModelNormalizer,
					getMpCtCalendarPricing: _getMpCtCalendarPricing,
					mapGetMerchantPackageEventDates: _modelGetMerchantPackageEventDates,
					mapGetMpCtCalendar: _modelGetMpCtCalendar,
					mapGetWizardMpEventDates: _modelGetWizardMpEventDates,
					normalizeModelGetKeywordCalendar: _normalizeModelGetKeywordCalendar,
					normalizeModelGetMerchantPackageEventDates: _normalizeModelGetMerchantPackageEventDates,
					normalizeGetMpCtCalendar: _normalizeModelGetMpCtCalendar,
					normalizeGetWizardMpEventDates: _normalizeModelGetWizardMpEventDates,
					modelGetKeywordCalendarPricing: _modelGetKeywordCalendarPricing
				},

				// METHODS
				mapGetKeywordCalendarPricing: _mapGetKeywordCalendarPricing,
				mapGetKeywordComboCreator: _mapGetKeywordComboCreator,
				mapGetMerchantPackageEventDatesPricing: _mapGetMerchantPackageEventDatesPricing,
				mapGetMpctCalendarPricing: _mapGetMpctCalendarPricing,
				mapGetParkHours: _mapGetParkHours,
				mapGetResourceKeywordCalendar: _mapGetResourceKeywordCalendar,
				mapGetWizardMpEventDates: _mapGetWizardMpEventDates,
				normalizeGetEventDatesByCalendar: _normalizeGetEventDatesByCalendar,
				normalizeGetKeywordCalendar: _normalizeGetKeywordCalendar,
				normalizeGetKeywordComboCreator: _normalizeGetKeywordComboCreator,
				normalizeGetMerchantPackageEventDates: _normalizeGetMerchantPackageEventDates,
				normalizeGetMpctCalendar: _normalizeGetMpctCalendar,
				normalizeGetParkHours: _normalizeGetParkHours,
				normalizeGetResourceKeywordCalendar: _normalizeGetResourceKeywordCalendar,
				normalizeGetWizardMpEventDates: _normalizeGetWizardMpEventDates,
				setCheapestPrice: _setCheapestPrice
				/* eslint-disable sort-keys */
			}
		};

		return service;

		/**
		 * Create the calendar time object
		 * @param {Object} time The time information
		 * @param {Date} date The date object
		 * @param {String} pkgId The pcakage id
		 * @param {Object} options The options
		 * @returns {Object} The options object
		 */
		function createCalendarTimeObject(time, date, pkgId, options) {
			var mainCt = Package.getMainCustomerType(pkgId),
				available = forceInt(time.available),
				capacity = forceInt(time.capacity),
				price = UtilsPricing.getPrice(mainCt.retail_amount, mainCt.tax_amount, mainCt.fee_total);

			var calendarTimeObject = {
				dataMap: {
					available: available === -1 ? Infinity : available,
					capacity: capacity,
					currency: $currency(Math.ceil(price), true),
					isLimited: service.getLimitedAvailability(available, time.capacity, options),
					isSoldOut: options.markAllAvailable ? false : (Number.isNaN(time.available) || forceInt(time.available) <= 0) && capacity > 0,
					packageId: pkgId,
					price: price
				},
				date: Utilities.transformDateWithTimeFromTimeString(date, time.time),
				timeString: time.time
			};

			// If the timeMap option is set, map the data
			if (validArray(options.timeMap)) {
				// Iterate through the timeMap
				options.timeMap.forEach(function iterateMap(map) {
					// Expects a string, but can also be an object with a property and responseKey
					var property = validObject(map) ? map.property : map,
						key = validObject(map) ? map.responseKey : map;

					// If the property exists in the time object, set it
					if (checkProp('time.' + key, time)) {
						calendarTimeObject.dataMap[property] = time[key];
					}
				});
			}

			if (time.used) {
				calendarTimeObject.dataMap.used = forceInt(time.used);
			}

			return calendarTimeObject;
		}

		/**
		 * Creates a calendar date object that uses the same model for all calls.
		 * @param {Object} dt The data
		 * @param {Object} options The options
		 * @returns {CalendarDate} The normalized date object
		 */
		function createCalendarDateObject(dt, options) {
			var price = getPrice(dt.totalRetail, dt.totalTax),
				available = +dt.available,
				calendarDateObject = {
					dataMap: {
						available: available === -1 ? Infinity : available,
						capacity: forceInt(dt.capacity),
						currency: $currency(price, true),
						isLimited: service.getLimitedAvailability(available, dt.capacity, options),
						isSoldOut: options.markAllAvailable ? false : Number.isNaN(dt.available) || available === 0,
						packageId: dt.packageId,
						price: price
					},
					date: dt.date instanceof Date ? AccessoCalendarService.createDate(dt.date) : mysqlToJsDate(dt.date)
				};

			if (dt.used) {
				calendarDateObject.dataMap.used = forceInt(dt.used);
			}

			if (validArray(dt.times)) {
				calendarDateObject.times = dt.times;
			}

			return calendarDateObject;
		}

		/**
		 * Filters the package list based same day characs
		 * @param {Number[]} pkgList - An array of package id's
		 * @param {Date} date - The current date the filter tests against
		 * @returns {Number[]} - The filtered package list
		 */
		function filterSameDayPackageList(pkgList, date) {
			// Ascertain if the passed date is the same as today
			var isToday = AccessoCalendarService.isDateEqual(date, AccessoCalendarService.createDate());
			var hasSameDay;

			// If the list only has 1 package id, return it
			if (pkgList.length === 1) {
				return pkgList;
			}

			// Determine if we have any packages with same day swap characs
			hasSameDay = pkgList.some(function checkSameDay(packageId) {
				var pkg = Package.getPackageByID(packageId);

				return checkProp('pkg.CHARACS.SnapCalendarSameDaySwap', pkg, 'getBoolean');
			});

			// Filter the package list based on the the criteria
			return pkgList.filter(function filterPackage(packageId) {
				var pkg = Package.getPackageByID(packageId);
				var isSameDay = checkProp('pkg.CHARACS.SnapCalendarSameDaySwap', pkg, 'getBoolean');

				// If it is today and we have same day characs, only return the same day packages, otherwise the opposite
				return (isToday && hasSameDay)
					? isSameDay
					: !isSameDay;
			});
		}

		/**
		 * Generates selectable dates for the given range. Uses existing data where possible
		 * @param {Object[]} data - existing normalized calendar data
		 * @param {Date} startDate - The start date
		 * @param {Date} endDate - The end date
		 * @param {Number} [price=0] - The default pricing
		 * @returns {Object[]} calendar data for the range
		 */
		function generateEmptyDaysForSet(data, startDate, endDate, price) {
			var numDaysInSet = Math.abs(AccessoCalendarService.getDifferenceData(startDate, endDate).days) + 1,
				setDays = AccessoCalendarService.getDates(startDate, numDaysInSet);

			return setDays.map(function iterateSetDays(day) {
				var existingDate = copy(data.find(function findExistingDate(dt) {
					return AccessoCalendarService.isDateEqual(dt.date, day);
				}));

				if (validObject(existingDate)) {
					return existingDate;
				}

				return service.createCalendarDateObject({
					available: -1,
					date: day,
					totalRetail: price || 0,
				}, {});
			});
		}

		/**
		 * Determines the aria label that will accompany the date field
		 * @protected
		 * @function
		 * @param {Date} date The date object
		 * @param {Object} data The date's additional data object
		 * @param {Object} locales Locale keys for aria descriptions
		 * @returns {String} The aria label
		 */
		function getAriaLabel(date, data, locales) {
			var label = $date(date, 'longDate');

			if (data.isSoldOut && localeExists(locales.section, locales.target.soldOut)) {
				label += ' ' + getLocale(locales.section, locales.target.soldOut, '@@MERCHANT_ID');
			} else {
				label += ' ' + data.currency;

				if (data.isLimited && localeExists(locales.section, locales.target.limitedAvailability)) {
					label += ' ' + getLocale(locales.section, locales.target.limitedAvailability, '@@MERCHANT_ID');
				}
			}

			return label;
		}

		/**
		 * Searches through the calendar data and returns the first date with availability
		 * @public
		 * @function
		 * @param {Object[]} data - The calendar data
		 * @param {Number} [minAvailable=1] - The minimum available amount
		 * @param {Boolean} [noDateNull] - If we should return null if no date is found
		 * @returns {Date|null} The first available date - or null if no date is found
		 */
		function getFirstAvailableDate(data, minAvailable, noDateNull) {
			var date,
				foundDate;
			// Force a minimum available amount
			minAvailable = minAvailable > 1 ? minAvailable : 1;

			date = data.find(function iterateData(dt) {
				return service.hasAvailability(data, dt.date, minAvailable);
			});

			foundDate = date ? date.date : null;

			// Return the first date with availability
			return noDateNull ? foundDate : AccessoCalendarService.createDate(foundDate);
		}

		/**
		 * Find out if availability is limited
		 * @public
		 * @param {Number} available The available amount
		 * @param {Number} capacity The total capacity amount
		 * @param {Object} options The options passed in
		 * @returns {Boolean} If it has limited availability or not
		 */
		function getLimitedAvailability(available, capacity, options) {
			var limitedThreshold = options.limitedThreshold,
				threshold;

			if (!limitedThreshold || limitedThreshold === '0' || available === Infinity || available === -1) {
				return false;
			}

			threshold = forceInt(limitedThreshold);

			if (Number.isNaN(threshold)) {
				threshold = 0;
			}

			return /%/.test(limitedThreshold) ? available / capacity * 100 <= threshold : threshold >= available;
		}

		/**
		 * Sets the limited availability flag
		 * @public
		 * @function
		 * @param {Object[]} data Our data
		 * @returns {Object[]} The same data but with the cheapest price flag
		 */
		function getLimitedAvailabilityThreshold() {
			var legendSetup;

			if (service._.limitedAvailabilityThreshold !== null) {
				return service._.limitedAvailabilityThreshold;
			}

			if (JSONValidator.validate('legend_setup', AppData.settings.legend_setup)) {
				legendSetup = JSON.parse(AppData.settings.legend_setup);
				// Set the threshold to the global config as a default
				if (checkProp('legend.limitedAvailability', legendSetup)) {
					service._.limitedAvailabilityThreshold = legendSetup.limitedAvailability;
					return service._.limitedAvailabilityThreshold;
				}
			}
			service._.limitedAvailabilityThreshold = 0;
			return service._.limitedAvailabilityThreshold;
		}

		/**
		 * Checks to see if a specific date has availability
		 * @public
		 * @function
		 * @param {Object[]} data - The normalized calendar data
		 * @param {Date} date - The date to check
		 * @param {Number} minAvailable - The minimum available amount
		 * @returns {Boolean} If the date has availability
		 */
		function hasAvailability(data, date, minAvailable) {
			// Force a minimum available amount
			minAvailable = minAvailable > 1 ? minAvailable : 1;

			// Find the date and see if it meets the availability requirements
			return data.some(function iterateData(item) {
				return AccessoCalendarService.isDateEqual(item.date, date) && (item.dataMap.available >= minAvailable || item.dataMap.available === -1);
			});
		}

		/**
		 * Checks to see if a normalized Data set contains a limited Date
		 * @public
		 * @function
		 * @param {Object[]} normalizedData Calendar Data that has been ran through the normalized function
		 * @param {Date} [startDate] The start date of a subsection to check - Must has end date
		 * @param {Date} [endDate] The end date of a subsection to check - Must has start date
		 * @throws {Error} Throws an error if bad parameters are sent
		 * @returns {Boolean} If the data contains a limited day or not
		 */
		function hasLimited(normalizedData, startDate, endDate) {
			// Variable to hold if can can ignore the startDate / endDate
			var bypassRange = true;

			if (!Array.isArray(normalizedData)) {
				throw new Error('hasLimited expects an array. ' + typeof normalizedData + ' was given.');
			} else if (startDate instanceof Date) {
				if (!(endDate instanceof Date)) {
					throw new Error('hasLimited expects endDate to be specified if startDate is set.');
				} else if(startDate > endDate) {
					throw new Error('hasLimited expects startDate to be earlier or the same date as endDate.');
				}

				// We have a valid startDate / endDate - don't bypass range checks
				bypassRange = false;
			}

			// Return if the data contains a date marked with limited availability
			return normalizedData.some(function iterateNormalizedDate(item) {
				return (
					bypassRange
					|| AccessoCalendarService.compareDates(startDate, item.date) === 0
					|| AccessoCalendarService.compareDates(endDate, item.date) === 0
					|| (startDate < item.date && item.date < endDate)
				) && item.dataMap.isLimited && !item.dataMap.isSoldOut;
			});
		}

		/**
		 * If the specified date is in the data
		 * @public
		 * @param {Date} date - The specific date to check
		 * @param {Object[]} data - The normalized calendar data
		 * @returns {Boolean} If the date is in the data
		 */
		function isDateInData(date, data) {
			// If the date or data is not valid
			if (!date || !validArray(data)) {
				// Return false
				return false;
			}

			// Ensure date is a date object
			date = AccessoCalendarService.createDate(date);

			// Return if the date is in the data
			return data.some(function iterateData(item) {
				return AccessoCalendarService.isDateEqual(item.date, date) && !checkProp('item.dataMap.isSoldOut', item, 'returnValue');
			});
		}

		/**
		 * Main funnel function to normalize data from the model
		 * @public
		 * @param {Object} modelData - The data from the model
		 * @param {String} callName - The name of the call
		 * @param {Object} [options] - The options for the specific function
		 * @returns {Object[]} - The normalized data
		 */
		function normalizeDataFromModel(modelData, callName, options) {
			var normFunc = service._.model.getModelNormalizer(callName),
				data = [];

			options = options || {};

			// If they haven't set a threshold, use the global default
			if (!checkProp('options.limitedThreshold', options)) {
				options.limitedThreshold = service.getLimitedAvailabilityThreshold();
			}

			if (angular.isFunction(normFunc)) {
				data = normFunc(copy(modelData), options);
			}

			return Utilities.sortArray(data, 'date');
		}

		/**
		 * Takes a response from a call and converts the response to fit the accesso calendar dates model
		 * @public
		 * @function
		 * @param {Object} rawData The raw response from the call
		 * @param {String} callName The name of the call
		 * @param {Object} options The options for the specific function
		 * @returns {Object[]} The formatted dates object
		 */
		function normalizeDataFromResponse(rawData, callName, options) {
			var fnc,
				data = [];

			options = options || {};

			// If they haven't set a threshold, use the global default
			if (!checkProp('options.limitedThreshold', options)) {
				options.limitedThreshold = service.getLimitedAvailabilityThreshold();
			}

			/* eslint-disable default-case */
			switch (callName.toLowerCase()) {
			case 'geteventdatesbycalendar':
				fnc = service._.normalizeGetEventDatesByCalendar;
				break;
			case 'getkeywordcalendar':
				fnc = service._.normalizeGetKeywordCalendar;
				break;
			case 'getkeywordcombocreator':
				fnc = service._.normalizeGetKeywordComboCreator;
				break;
			case 'getmerchantpackageeventdates':
				fnc = service._.normalizeGetMerchantPackageEventDates;
				break;
			case 'getmpctcalendar':
				fnc = service._.normalizeGetMpctCalendar;
				break;
			case 'getresourcekeywordcalendar':
				fnc = service._.normalizeGetResourceKeywordCalendar;
				break;
			case 'getwizardmpeventdates':
				fnc = service._.normalizeGetWizardMpEventDates;
				break;
			case 'getparkhours':
				fnc = service._.normalizeGetParkHours;
				break;
			}
			/* eslint-disable default-case */

			if (fnc) {
				data = fnc(rawData, options);
			}

			return Utilities.sortArray(data, 'date');
		}

		/**
		 * Sets the cheapest price flag
		 * @private
		 * @param {Object[]} data Our data
		 * @returns {Object[]} The same data but with the cheapest price flag
		 */
		function _setCheapestPrice(data) {
			var cheapestPrice,
				hasMinimumTwoPrices;

			if (!validArray(data)) {
				return [];
			}

			cheapestPrice = data.reduce(function cheapestPriceDay(cheapestPrice, day) {
				if (day.dataMap && day.dataMap.price < cheapestPrice) {
					return day.dataMap.price;
				}

				return cheapestPrice;
			}, Infinity);

			// If we couldn't find a cheapest price, stop.
			if (cheapestPrice === Infinity) {
				return data;
			}

			// In order for us to show the cheapest price, we have to have at least two different prices
			hasMinimumTwoPrices = data.some(function hasOtherPrice(day) {
				return day.dataMap && day.dataMap.price !== cheapestPrice;
			});

			// If they're all the same price, we don't want to set every one of them to cheapest
			if (hasMinimumTwoPrices) {
				data.forEach(function setCheapestPriceFlag(day) {
					if (day.dataMap && day.dataMap.price === cheapestPrice) {
						day.dataMap.cheapest = true;
					}
				});
			}

			return data;
		}

		/**
		 * Gets the function to normalize the specified call
		 * @private
		 * @param {String} callName - The name of the call
		 * @returns {Function} The function to normalize the data
		 */
		function _getModelNormalizer(callName) {
			/* eslint-disable default-case */
			switch (callName.toLowerCase()) {
				case 'geteventdatesbycalendar':
					break;
				case 'getkeywordcalendar':
					return service._.model.normalizeModelGetKeywordCalendar;
				case 'getkeywordcombocreator':
					break;
				case 'getmerchantpackageeventdates':
					return service._.model.normalizeModelGetMerchantPackageEventDates;
				case 'getmpctcalendar':
					return service._.model.normalizeGetMpCtCalendar;
				case 'getresourcekeywordcalendar':
					break;
				case 'getwizardmpeventdates':
					return service._.model.normalizeGetWizardMpEventDates;
				case 'getparkhours':
					return service._.normalizeGetParkHours;
				}
		}

		/**
		 * Gets the pricing for a specific package and customer type
		 * @private
		 * @param {Number} packageId - The package ID
		 * @param {Object[]} customers - The customer types
		 * @returns {Object} - The pricing object
		 */
		function _getMpCtCalendarPricing(packageId, customers) {
			return customers.reduce(function iterateCustomerType(acc, ct) {
				acc.price += (DynamicCapacityPricingService.getPrice(packageId, ct, {
					fees: 'fee',
					price: 'total',
					tax: '#ignore',
				}));

				acc.tax += checkProp('ct.dynamic.tax', ct, 'returnValue') || 0;

				return acc;
			}, { price: 0, tax: 0, });
		}

		/**
		 * Normalizes the GetKeywordCalendar pricing
		 * @protected
		 * @param {Object[]} dates The dates from backend.
		 * @returns {Object[]} The mapping?
		 */
		function _mapGetKeywordCalendarPricing(dates) {
			var mappedDates = [];

			// Do we have a valid array of dates to work with?
			if (!validArray(dates)) {
				// If not, return empty array
				return mappedDates;
			}

			dates.forEach(function iterateDates(date) {
				var cheapest;

				if (checkProp('date.PS.P', date)) {
					cheapest = wrapToArray(date.PS.P, true).reduce(function reducePackageToLowest(lowestPkg, currentPkg) {
						var lowestMainCt,
							lowestCtFromResponse,
							lowestPrice,
							currentMainCt,
							currentCtFromResponse,
							currentPrice,
							foundPackage;

						// Set the current lowest price as if there's only one, we need to be able to grab the price
						currentPkg.CT = wrapToArray(currentPkg.CT);
						currentMainCt = Package.getMainCustomerType(currentPkg.id);
						currentCtFromResponse = currentMainCt ? currentPkg.CT.find(filterById(currentMainCt.id)): null;

						// If we can't find the lowest main ct in the response, grab the first one
						if (!currentCtFromResponse) {
							currentCtFromResponse = currentPkg.CT[0];
							foundPackage = Package.getPackageByID(currentPkg.id);
							//if package isn't in the main keyword list - then return null so we can gracefully bow out
							if (foundPackage) {
								currentMainCt = foundPackage.CT.find(filterById(currentCtFromResponse.id));
							} else {
								return lowestPkg;
							}
						}

						// Get dynamic pricing if we have any
						currentPrice = DynamicCapacityPricingService.getPrice(currentPkg.id, currentCtFromResponse);
						currentPkg.__accessoCalendarPrice = currentPrice;

						if (!lowestPkg) {
							return currentPkg;
						}

						lowestPkg.CT = wrapToArray(lowestPkg.CT);
						lowestMainCt = Package.getMainCustomerType(lowestPkg.id);
						lowestCtFromResponse = lowestPkg.CT.find(filterById(lowestMainCt.id));

						// If we can't find the lowest main ct in the response, grab the first one
						if (!lowestCtFromResponse) {
							lowestCtFromResponse = lowestPkg.CT[0];
							lowestMainCt = Package.getPackageByID(lowestPkg.id).CT.find(filterById(lowestCtFromResponse.id));
						}

						// Get dynamic pricing if we have any
						lowestPrice = DynamicCapacityPricingService.getPrice(lowestPkg.id, lowestCtFromResponse);
						lowestPkg.__accessoCalendarPrice = lowestPrice;

						return (lowestPrice > currentPrice && (forceInt(lowestPkg.available) === 0 || forceInt(currentPkg.available) !== 0))
							? currentPkg
							: lowestPkg;
					}, null);

					if (!cheapest) {
						return;
					}

					mappedDates.push({
						available: forceInt(cheapest.available),
						capacity: forceInt(cheapest.capacity),
						packageId: cheapest.id,
						date: date.date,
						totalRetail: cheapest.__accessoCalendarPrice,
						totalTax: 0
					});
				}
			});

			return mappedDates;
		}

		/**
		 * Normalizes the GetKeywordComboCreator pricing
		 * @protected
		 * @param {Object[]} dates The dates from backend.
		 * @returns {Object[]} The mapping?
		 */
		function _mapGetKeywordComboCreator(dates) {
			var mappedDates = [];

			// Do we have a valid array of dates to work with?
			if (!validArray(dates)) {
				// If not, return empty array
				return mappedDates;
			}

			dates.forEach(function iterateDates(date) {
				var cheapest;

				if (checkProp('date.PS.P', date)) {
					cheapest = wrapToArray(date.PS.P, true).reduce(function reducePackageToLowest(lowestPkg, currentPkg) {
						var lowestMainCt,
							lowestCtFromResponse,
							lowestPrice,
							currentMainCt,
							currentCtFromResponse,
							currentPrice;

						// Set the current lowest price as if there's only one, we need to be able to grab the price
						currentPkg.CT = wrapToArray(currentPkg.CT);
						currentMainCt = Package.getMainCustomerType(currentPkg.id);
						currentCtFromResponse = currentPkg.CT.find(filterById(currentMainCt.id));

						// If we can't find the lowest main ct in the response, grab the first one
						if (!currentCtFromResponse) {
							currentCtFromResponse = currentPkg.CT[0];
							currentMainCt = Package.getPackageByID(currentPkg.id).CT.find(filterById(currentCtFromResponse.id));
						}

						// Get dynamic pricing if we have any
						currentPrice = DynamicCapacityPricingService.getPrice(currentPkg.id, currentCtFromResponse);
						currentPkg.__accessoCalendarPrice = currentPrice;

						if (!lowestPkg) {
							return currentPkg;
						}

						lowestPkg.CT = wrapToArray(lowestPkg.CT);
						lowestMainCt = Package.getMainCustomerType(lowestPkg.id);
						lowestCtFromResponse = lowestPkg.CT.find(filterById(lowestMainCt.id));

						// If we can't find the lowest main ct in the response, grab the first one
						if (!lowestCtFromResponse) {
							lowestCtFromResponse = lowestPkg.CT[0];
							lowestMainCt = Package.getPackageByID(lowestPkg.id).CT.find(filterById(lowestCtFromResponse.id));
						}

						// Get dynamic pricing if we have any
						lowestPrice = DynamicCapacityPricingService.getPrice(lowestPkg.id, lowestCtFromResponse);
						lowestPkg.__accessoCalendarPrice = lowestPrice;

						return (lowestPrice > currentPrice && (forceInt(lowestPkg.available) === 0 || forceInt(currentPkg.available) !== 0))
							? currentPkg
							: lowestPkg;
					}, null);

					if (!cheapest) {
						return;
					}

					// Handle case where A is an array of time slots with availability
					var available = 0;
					if (Array.isArray(cheapest.CT[0].A)) {
						// Sum all available slots or use the first one depending on requirements
						// Using the first available time slot here since that's what the calendar shows first
						if (cheapest.CT[0].A.length > 0) {
							available = forceInt(cheapest.CT[0].A[0].available);
						}
					} else if (cheapest.CT[0].A && cheapest.CT[0].A.available) {
						available = forceInt(cheapest.CT[0].A.available);
					}

					mappedDates.push({
						available: available,
						capacity: available,
						packageId: cheapest.id,
						date: date.date,
						totalRetail: cheapest.__accessoCalendarPrice,
						totalTax: 0
					});
				}
			});

			return mappedDates;
		}

		/**
		 * Normalizes the GetMerchantPackageEventDates calendar pricing
		 * @protected
		 * @param {Object} dates The dates from backend.
		 * @param {Object} options Any options
		 * @returns {Object[]} The mapping?
		 */
		function _mapGetMerchantPackageEventDatesPricing(dates, options) {
			var mappedDates = [];

			// Do we have a valid array of dates to work with?
			if (!validArray(dates)) {
				// If not, return empty array
				return mappedDates;
			}

			return dates.map(function iterateDates(date) {
				var packageId = options.masterPackageId || date.package_id,
					mainCt = Package.getMainCustomerType(packageId),
					currentDate = Utilities.dateStringToObject(date.date),
					model = {
						available: 0,
						capacity: 0,
						date: date.date,
						dynamicPricing: !!mainCt.DYNAMIC_PRICING,
						packageId: date.package_id,
						times: [],
						totalRetail: mainCt.retail_amount,
						totalTax: mainCt.tax_amount
					},
					lowestTime;

				if (checkProp('date.T', date)) {
					model.times = Utilities.sortArray(
						wrapToArray(date.T, true).map(function createTimeObject(time) {
							return service.createCalendarTimeObject(time, currentDate, packageId, options);
						}),
						'date'
					);
				}

				lowestTime = model.times.reduce(function findTheLowest(lowest, time) {
					var timeAvailable = checkProp('time.dataMap.available', time, 'returnValue');
					timeAvailable = timeAvailable === -1 ? Infinity : timeAvailable;

					if (timeAvailable && (lowest === null || lowest.dataMap.available > time.dataMap.available)) {
						return time;
					}

					return lowest;
				}, null);

				if (lowestTime) {
					model.available = lowestTime.dataMap.available;
					model.capacity = lowestTime.dataMap.capacity;
				}

				return model;
			});
		}

		/**
		 * Normalizes the GetMPCTCalendar calendar pricing
		 * @protected
		 * @param {TBA} dates The dates from backend.
		 * @returns {TBA} The mapping?
		 */
		function _mapGetMpctCalendarPricing(dates) {
			var mappedDates = [];

			// Do we have a valid array of dates to work with?
			if (!validArray(dates)) {
				// If not, return empty array
				return mappedDates;
			}

			dates.forEach(function iterateDates(date) {
				var cheapestItem = {},
					totalRetail = 0,
					totalTax = 0;

				if (checkProp('date.PS.P.CT', date)) {
					wrapToArray(date.PS.P.CT, true).forEach(function iterateCustomerTypes(ct) {
						if (!validObject(ct.DYNAMIC_PRICING)) {
							return;
						}

						totalRetail += parseFloat(ct.DYNAMIC_PRICING.total_amount);
						totalTax += parseFloat(ct.DYNAMIC_PRICING.tax);
					});

					mappedDates.push({
						available: forceInt(date.available),
						capacity: forceInt(date.available),
						packageId: date.PS.P.id,
						date: date.date,
						totalRetail: totalRetail,
						totalTax: totalTax
					});
				} else if (checkProp('date.PS.P.T', date)) {
					wrapToArray(date.PS.P.T, true).forEach(function(time) {
						var available = forceInt(time.available),
							item;

						if (!time || Number.isNaN(available) || available <= 0) {
							return;
						}

						wrapToArray(time.CT, true).forEach(function iterateCustomerTypes(ct) {
							if (!validObject(ct.DYNAMIC_PRICING)) {
								return;
							}

							totalRetail += parseFloat(ct.DYNAMIC_PRICING.total_amount);
							totalTax += parseFloat(ct.DYNAMIC_PRICING.tax);
						});

						item = {
							qty: time.available ? forceInt(time.available) : false,
							time: time.time,
							totalRetail: totalRetail,
							totalTax: totalTax
						};

						if (!validObject(cheapestItem) || cheapestItem.totalRetail > totalRetail) {
							cheapestItem = item;
						}
					});

					mappedDates.push({
						available: forceInt(date.available),
						capacity: forceInt(date.available),
						date: date.date,
						packageId: date.PS.P.id,
						totalRetail: cheapestItem.totalRetail || 0,
						totalTax: cheapestItem.totalTax || 0
					});
				}
			});

			return mappedDates;
		}

		/**
		 * Normalizes the _mapGetWizardMPEventDates calendar pricing
		 * @protected
		 * @param {Object} dates The dates from backend.
		 * @param {Object} packageId The packageId searched
		 * @returns {Object[]} The mapping
		 */
		function _mapGetWizardMpEventDates(dates, packageId) {
			return dates.map(function iterateDates(dt) {
				return {
					available: forceInt(dt.capacity),
					capacity: forceInt(dt.capacity),
					date: dt.date,
					packageId: packageId
				};
			});
		}

		/**
		 * Normalizes the GetParkHours array
		 * @param {Object[]} dates The dates that came back
		 * @returns {Object[]} The mapping
		 */
		function _mapGetParkHours(dates) {
			return dates.map(function iterateParkDay(dt) {
				return {
					available: '-1',
					capacity: '1',
					date: dt.FormatDate.replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3'),
					packageId: '-1',
					totalRetail: 0,
					totalTax: 0
				}
			});
		}

		/**
		 * Normalizes the GetResourceKeywordCalendar
		 * @protected
		 * @param {Object[]} dates - The raw data for the D Object
		 * @returns {Object[]} - Normalized data for consumption for the calendar
		 */
		function _mapGetResourceKeywordCalendar(dates) {
			var mappedDates = [];

			// Do we have a valid array of dates to work with?
			wrapToArray(dates, true).forEach(function iterateDates(date) {
				var capacity = 0;
				var available = 0;

				// Find the cheapest group for a specific date
				var cg = wrapToArray(date.RESOURCE_GROUPS.RESOURCE_GROUP, true).reduce(function findTheCheapest(cheapestGroup, resourceGroup) {
					// Find the cheapest resource for a specific group
					var cr = wrapToArray(resourceGroup.RESOURCE, true).reduce(function findTheCheapestResource(cheapest, resource) {
						var cheapestCt;

						// If the resource has no capacity
						if (resource.capacity === '0') {
							// Assume group capacity is 1 and increment -- This is treated as the number available to the calendar
							capacity++;

							// Return the cheapest
							return cheapest;
						}

						// Increment capacity
						capacity += forceInt(resource.capacity);

						// Increment available by capacity
						available += forceInt(resource.capacity);

						// Find the cheapest customer type for a specific resource
						cheapestCt = wrapToArray(resource.P.CT, true).reduce(function findTheCheapestCt(acc, ct) {
							// If the cheapest ct has not been set or if the current ct is cheaper than the current cheapest, then set current ct to be the current cheapest
							if (!acc || acc.totalRetail > ct.retail_amount) {
								return {
									totalRetail: ct.retail_amount,
									totalTax: ct.tax_amount
								};
							}

							// Otherwise return current cheapest
							return acc;
						}, null);

						// if no cheapest customer type was found or current ct is cheaper than the current cheapest customer type
						if (cheapestCt && (!cheapest || parseFloat(cheapest.totalRetail) > parseFloat(cheapestCt.totalRetail))) {
							// Set cheapest as current ct
							return {
								available: resource.capacity,
								capacity: resource.capacity,
								packageId: resource.P.id,
								totalRetail: cheapestCt.totalRetail,
								totalTax: cheapestCt.totalTax
							};
						}

						// return current cheapest
						return cheapest;
					}, null);

					// If no cheapest resource could be determined
					if (!cr) {
						// Return the current cheapest group
						return cheapestGroup;
					}

					// return the cheapest group
					return !cheapestGroup || parseFloat(cr.totalRetail) < parseFloat(cheapestGroup.totalRetail) ? cr : cheapestGroup;
				}, null);

				// If we have a valid cheapest group
				if (cg) {
					// Add date to the mapped dates
					mappedDates.push({
						available: available,
						capacity: capacity,
						date: date.date,
						packageId: cg.packageId,
						totalRetail: cg.totalRetail,
						totalTax: cg.totalTax
					});
				}
			});

			// Return the mapped dates
			return mappedDates;
		}

		/**
		 * Normalizes the GetKeywordCalendar call from model data
		 * @protected
		 * @param {Object[]} modelData - The model data
		 * @param {Object} options - The options
		 * @returns {Object[]} The normalized data which contains the cheapest package for each date
		 */
		function _modelGetKeywordCalendarPricing(modelData, options) {
			var mappedDates = [],
				cheapest,
				currentPrice = 0,
				currentCtFromResponse,
				currentMainCt,
				lowestPrice,
				currentAvailable,
				lowestAvailable;

			if (!validArray(modelData)) {
				return mappedDates; // Return an empty array if modelData is not valid
			}

			modelData.forEach(function (dateEntry) {
				// Ensure there are packages to evaluate
				if (validArray(dateEntry.packages)) {
					cheapest = dateEntry.packages.reduce(function (lowestPkg, currentPkg) {
						// Wrap the customer types in an array
						currentPkg.customer = wrapToArray(currentPkg.customer);

						// Get the main customer type for this package
						currentMainCt = Package.getMainCustomerType(currentPkg.id);

						// Try to find the main customer type in the response
						currentCtFromResponse = currentPkg.customer.find(filterById(currentMainCt.id));

						// If not found, use the first customer type in the array
						if (!currentCtFromResponse) {
							currentCtFromResponse = currentPkg.customer[0];
							currentMainCt = Package.getPackageByID(currentPkg.id).CT.find(filterById(currentCtFromResponse.id));
						}

						// Use dynamic capacity pricing to calculate the price
						currentPrice = DynamicCapacityPricingService.getPrice(currentPkg.id, currentCtFromResponse);

						// Assign the calculated price to the package
						currentPkg.__accessoCalendarPrice = currentPrice;

						// If there's no current lowest package, initialize with the current one
						if (!lowestPkg) {
							return currentPkg;
						}

						// Compare the prices
						lowestPrice = lowestPkg.__accessoCalendarPrice;
						currentAvailable = forceInt(currentPkg.event.available);
						lowestAvailable = forceInt(lowestPkg.event.available);

						return (currentPrice < lowestPrice || (lowestAvailable === 0 && currentAvailable > 0)) ? currentPkg : lowestPkg;
					}, null);

					// If no package was determined to be cheapest, skip this date
					if (!cheapest) {
						return;
					}

					// Add the processed date with the cheapest package to the results
					mappedDates.push({
						available: forceInt(cheapest.event.available),
						capacity: forceInt(cheapest.event.capacity),
						packageId: String(cheapest.id),
						date: dateEntry.date,
						totalRetail: cheapest.__accessoCalendarPrice,
						totalTax: forceInt(cheapest.totalTax) || 0,
					});
				}
			});

			return mappedDates;
		}

		/**
		 * Normalize the Get Merchant Package Event Dates calls from data model
		 * @param {Object[]} modelDates - The model data dates
		 * @param {Object} options - The options
		 * @returns {Object[]} The normalized data
		 */
		function _modelGetMerchantPackageEventDates(modelDates, options) {
			return modelDates.map(function iterateDates(date) {
				var pkgId = options.masterPackageId || Package.getLowestPriced(service.filterSameDayPackageList(date.package.id, date.date));
				var lowestTime = first(date.times.sort(function findLowestTime(a, b) {
					var aQty, bQty;

					if (a.pricing && b.pricing) {
						return a.pricing.retail - b.pricing.retail;
					} else if (a.pricing) {
						return -1;
					} else if (b.pricing) {
						return 1;
					}

					aQty = a.available <= 0 ? Infinity : a.available;
					bQty = b.available <= 0 ? Infinity : b.available;

					return aQty - bQty;
				}));

				var mainCt = Package.getMainCustomerType(pkgId);
				var retail = checkProp('lowestTime.pricing.retail', lowestTime, 'returnValue') || mainCt.retail_amount;
				var tax = checkProp('lowestTime.pricing.tax', lowestTime, 'returnValue') || mainCt.tax_amount;

				return {
					available: lowestTime.available,
					capacity: lowestTime.capacity,
					date: date.date,
					duration: lowestTime.duration,
					packageId: pkgId,
					totalRetail: retail,
					totalTax: tax,
				};
			});
		}

		/**
		 * Normalizes the GetMpCtCalendar calls from model data
		 * @protected
		 * @param {Object} modelData - The model data
		 * @param {Object} options - The options
		 * @returns - The normalized data
		 */
		function _modelGetMpCtCalendar(modelData, options) {
			return modelData.dates.map(function iterateDates(dt) {
				return dt.packages.reduce(function findLowestPricing(acc, pkg) {
					var customer,
						datePricing;

					if (options.isTimed) {
						customer = pkg.times.reduce(function findLowestTime(acc, time) {
							var timePricing;

							// No availability, skip
							if (time.available === 0) {
								return acc;
							}

							// Get the aggregate pricing for the time
							timePricing = service._.model.getMpCtCalendarPricing(pkg.id, time.customer);

							// If we don't have a lowest price or the current time is cheaper than the lowest price
							if (acc === null || timePricing.price < acc._pricing.price) {
								// return a copy of time with the pricing object
								return merge(copy(time), { _pricing: timePricing });
							}

							// Otherwise return the existing lowest price
							return acc;
						}, null);
					// If the date has availability
					} else if (dt.available !== 0) {
						datePricing = service._.model.getMpCtCalendarPricing(pkg.id, pkg.customer);
						customer = merge(copy(pkg.customer), { _pricing: datePricing });
					}

					return {
						available: dt.available,
						capacity: dt.available,
						date: dt.date,
						packageId: pkg.id,
						totalRetail: customer ? customer._pricing.price : 0,
						totalTax: customer ? customer._pricing.tax : 0,
					};
				}, null);
			});
		}

		/**
		 * Normalizes the GetWizardMpEventDates from model data
		 * @protected
		 * @param {Object} modelData - The model data
		 * @param {Object} options - The options
		 * @returns {Object[]} The normalized data
		 */
		function _modelGetWizardMpEventDates(modelData, options) {
			var packageId = options.masterPackageId || modelData.package.id,
				mainCt = Package.getMainCustomerType(packageId);

			return modelData.dates.map(function iterateDates(date) {
				return {
					available: date.capacity,
					capacity: date.capacity,
					date: date.date,
					packageId: packageId,
					totalRetail: mainCt.retail_amount,
					totalTax: mainCt.tax_amount
				}
			});
		}

		/**
		 * Normalizes the GetEventDatesByCalendar
		 * @todo
		 * @protected
		 * @param {Object} rawData The raw response from backend
		 * @returns {CalendarDate[]}} The normalized response
		 */
		function _normalizeGetEventDatesByCalendar(rawData) {
			var formattedDates = [];
		}

		/**
		 * Normalizes the GetMerchantPackageEventDates
		 * @protected
		 * @param {Object} rawData The raw response from backend.
		 * @param {Object} options The available options you can send.
		 * @returns {CalendarDate[]}} The normalized response
		 */
		function _normalizeGetMerchantPackageEventDates(rawData, options) {
			var dates;

			// Check to see if the dates object exists
			if (checkProp('rawData.D', rawData)) {
				dates = wrapToArray(rawData.D, true);
			}

			// Check to see if we do not have valid data
			if (!validArray(dates)) {
				return [];
			}

			dates = service._.mapGetMerchantPackageEventDatesPricing(dates, options);

			return dates.map(function iterateDates(dt) {
				return service.createCalendarDateObject(dt, options);
			});
		}

		/**
		 * Normalizes the GetKeywordCalendar
		 * @protected
		 * @param {Object} rawData The raw response from backend.
		 * @param {Object} options The available options you can send.
		 * @returns {CalendarDate[]}} The normalized response
		 */
		function _normalizeGetKeywordCalendar(rawData, options) {
			var dates;

			// Check to see if the dates object exists
			if (checkProp('rawData.DATES.D', rawData)) {
				dates = wrapToArray(rawData.DATES.D, true);
			}

			// Check to see if we do not have valid data
			if (!validArray(dates)) {
				// return an empty array
				return [];
			}
			dates = service._.mapGetKeywordCalendarPricing(dates);
			return dates.map(function iterateDates(dt) {
				return service.createCalendarDateObject(dt, options);
			});
		}

		/**
		 * Normalizes the GetKeywordComboCreator
		 * @protected
		 * @param {Object} rawData The raw response from backend.
		 * @param {Object} options The available options you can send.
		 * @returns {CalendarDate[]}} The normalized response
		 */
		function _normalizeGetKeywordComboCreator(rawData, options) {
			var dates;

			// Check to see if the dates object exists
			if (checkProp('rawData.DATES.D', rawData)) {
				dates = wrapToArray(rawData.DATES.D, true);
			}

			// Check to see if we do not have valid data
			if (!validArray(dates)) {
				// return an empty array
				return [];
			}

			dates = service._.mapGetKeywordComboCreator(dates);

			return dates.map(function iterateDates(dt) {
				return service.createCalendarDateObject(dt, options);
			});
		}


		/**
		 * Normalizes the GetMPCTCalendar (a.k.a. PackagePlanner)
		 * @protected
		 * @param {Object} rawData The raw response from backend.
		 * @param {Object} options The available options you can send.
		 * @returns {CalendarDate[]}} The normalized response
		 */
		function _normalizeGetMpctCalendar(rawData, options) {
			var dates;

			// Check to see if the dates object exists
			if (checkProp('rawData.DATES.D', rawData)) {
				dates = wrapToArray(rawData.DATES.D, true);
			}

			// Check to see if we do not have valid data
			if (rawData.has_available_dates !== '1' || !validArray(dates)) {
				// return an empty array
				return [];
			}

			dates = service._.mapGetMpctCalendarPricing(dates);

			return dates.map(function iterateDates(dt) {
				return service.createCalendarDateObject(dt, options);
			});
		}

		/**
		 * Normalizes the GetResourceKeywordCalendar which is used with resources (a.k.a. Cabanas)
		 * @todo
		 * @protected
		 * @param {Object} rawData The raw response from backend.
		 * @returns {CalendarDate[]}} The normalized response
		 */
		function _normalizeGetResourceKeywordCalendar(rawData, options) {
			var dates;

			// Check to see if the dates object exists
			if (checkProp('rawData.DATES.D', rawData)) {
				dates = wrapToArray(rawData.DATES.D, true);
			}

			dates = service._.mapGetResourceKeywordCalendar(dates);

			return dates.map(function iterateDates(dt) {
				return service.createCalendarDateObject(dt, options);
			});
		}

		/**
		 * Normalizes the GetWizardMPEventDates (a.k.a. Date Wizard - uses date_time)
		 * @protected
		 * @param {Object} rawData The raw response from backend.
		 * @param {Object} options The available options you can send.
		 * @returns {CalendarDate[]}} The normalized response
		 */
		function _normalizeGetWizardMpEventDates(rawData, options) {
			var dates = checkProp('rawData.D', rawData, [wrapToArray, true]);

			if (!validArray(dates)) {
				return [];
			}

			dates = service._.mapGetWizardMpEventDates(dates, rawData.package_id);

			return dates.map(function iterateDates(dt) {
				return service.createCalendarDateObject(dt, options);
			});
		}

		/**
		 * Normalizes the GetMpCtCalendar
		 * @protected
		 * @param {Object} modelData - The model data
		 * @param {Object} options - The options passed in
		 * @returns {Object[]} - The normalized response
		 */
		function _normalizeModelGetMpCtCalendar(modelData, options) {
			var dates;

			if (!modelData.flags.hasDates || modelData.status !== Enums.status.OK) {
				return [];
			}

			options = Utilities.merge({}, options, {
				isTimed: modelData.flags.isTimed,
			});

			dates = service._.model.mapGetMpCtCalendar(modelData, options);

			return dates.map(function iterateDates(dt) {
				return service.createCalendarDateObject(dt, options);
			});
		}

		/**
		 * Normalizes the GetParkHours
		 * @protected
		 * @param {Object} rawData The raw response from backend.
		 * @param {Object} options The available options you can send.
		 * @returns {CalendarDate[]}} The normalized response
		 */
		function _normalizeGetParkHours(rawData, options) {
			var newData = validArray(rawData) ? rawData[0] : rawData;
			var dates = checkProp('rawData.Days.Day', newData, [ wrapToArray, true ]);

			if (!validArray(dates)) {
				return [];
			}

			dates = service._.mapGetParkHours(dates);

			return dates.map(function iterateDates(dt) {
				return service.createCalendarDateObject(dt, options);
			});
		}

		function _normalizeModelGetKeywordCalendar(modelData, options) {
			var dates = modelData.dates,
				transformedDates,
				options = options || {};

			if (!validArray(dates)) {
				return [];
			}

			transformedDates = service._.model.modelGetKeywordCalendarPricing(dates, options);

			return transformedDates.map(function iterateDates(dateEntry) {
				return service.createCalendarDateObject(dateEntry, options);
			});
		}

		/**
		 * Normalizes the GetMerchantPackageEventDates to a model
		 * @protected
		 * @param {Object} modelData - The model data
		 * @param {Object} options - The options passed in
		 * @returns {Object[]} The normalized calendar date object
		 */
		function _normalizeModelGetMerchantPackageEventDates(modelData, options) {
			var dates = modelData.dates;

			if (!validArray(dates)) {
				return [];
			}

			dates = service._.model.mapGetMerchantPackageEventDates(dates, options);

			return dates.map(function iterateDates(dt) {
				return service.createCalendarDateObject(dt, options);
			});
		}

		/**
		 * Converts the raw data from the backend to a normalized calendar date object
		 * @protected
		 * @function
		 * @param {Object} modelData The modelData object
		 * @param {Object} options The options passed in
		 * @returns {Object} The normalized calendar date object
		 */
		function _normalizeModelGetWizardMpEventDates(modelData, options) {
			var dates;

			if (!validArray(modelData.dates)) {
				return [];
			}

			dates = service._.model.mapGetWizardMpEventDates(modelData, options);

			return dates.map(function iterateDates(dt) {
				return service.createCalendarDateObject(dt, options);
			});
		}
	}
})();